/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { VIEW_KIND_TREE } from '@jira/polaris__domain-view/src/viewKindTimeline.constants.tsx';
import type { ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { useOnboardingFlowsActions } from '@jira/polaris__lib-onboarding-flows/src/controllers/index.tsx';
import { useIsNavigationLocked } from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import { TreeViewOnboardingSpotlight } from '@jira/polaris__lib-onboarding-flows/src/ui/tree-view-onboarding/spotlights/TreeViewOnboardingSpotlight.tsx';
import AddIcon from '@atlaskit/icon/core/add';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { CreateViewMenu, type TriggerProps } from '../../../create-view-menu/CreateViewMenu.tsx';
import {
	useAnalyticsL2,
	useContainerId,
	useJpdNavCanManageViews,
} from '../../../../controllers/context.tsx';
import { isNavigationProject, type NavigationContainer } from '../../../../common/types.tsx';

const createView = defineMessage({
	defaultMessage: 'Create a view',
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.create-view.create-view',
	description: 'Label for the view creation button',
});

type CreateViewProps = {
	container?: string;
	// Server id of the parent section, use it to properly nest newly created views
	sectionId?: string;
	navigationContainer: NavigationContainer;
	// Used to show the spotlight on just the footer create view buttons
	enableSpotlight?: boolean;
};

export const CreateViewMenuItem = ({
	container,
	sectionId,
	navigationContainer,
	enableSpotlight = false,
}: CreateViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const containerId = useContainerId();
	const [isNavigationLocked] = useIsNavigationLocked({ containerId });
	const canManageViews = useJpdNavCanManageViews();
	const { sendCreateViewOnViewNavigationItemAnalyticsEvent } = useAnalyticsL2();

	const { createView: createViewAction } = useNavigationActions();
	const { setTreeViewOnboardingSpotlightDismissed } = useOnboardingFlowsActions();

	const handleAnalyticsEvent = useCallback(() => {
		sendCreateViewOnViewNavigationItemAnalyticsEvent(navigationContainer);
	}, [navigationContainer, sendCreateViewOnViewNavigationItemAnalyticsEvent]);

	const handleCreateTreeView = useCallback(() => {
		// Directly create a tree view used by the spotlight "Create" button
		createViewAction(VIEW_KIND_TREE, container);
		handleAnalyticsEvent();
	}, [createViewAction, container, handleAnalyticsEvent]);

	const handleMenuItemClick = useCallback(
		({ type }: { type: ViewKind }) => {
			// Dismiss the onboarding spotlight if the user creates a tree view via the dropdown
			if (type === VIEW_KIND_TREE) {
				setTreeViewOnboardingSpotlightDismissed();
			}
		},
		[setTreeViewOnboardingSpotlightDismissed],
	);

	const renderTrigger = useCallback(
		({ onClick, ...rest }: TriggerProps) => (
			<ButtonMenuItem
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.menu-items.views.create-view.menu-button-item"
				{...rest}
				elemBefore={<AddIcon label={formatMessage(createView)} color="currentColor" size="small" />}
				isDisabled={isNavigationLocked || !canManageViews}
				onClick={() => {
					onClick();
					handleAnalyticsEvent();
				}}
			>
				{formatMessage(createView)}
			</ButtonMenuItem>
		),
		[canManageViews, formatMessage, handleAnalyticsEvent, isNavigationLocked],
	);

	if (enableSpotlight) {
		return (
			<TreeViewOnboardingSpotlight
				isNonProjectContainer={!isNavigationProject(navigationContainer)}
				onCreateTreeView={handleCreateTreeView}
			>
				<CreateViewMenu
					navigationContainer={navigationContainer}
					container={container}
					sectionId={sectionId}
					renderTrigger={renderTrigger}
					onMenuItemClick={handleMenuItemClick}
				/>
			</TreeViewOnboardingSpotlight>
		);
	}

	return (
		<CreateViewMenu
			navigationContainer={navigationContainer}
			container={container}
			sectionId={sectionId}
			renderTrigger={renderTrigger}
			onMenuItemClick={handleMenuItemClick}
		/>
	);
};
